// Tabbed drill-down (router-free, unit-testable). Active tab is a route param
// (not state) so /sites/demo1/logs is linkable/refresh-safe/back-navigable;
// SiteDrilldown wrapper passes tab in / onTabChange out.
import { useState } from 'react';
import { StatusTab } from './tabs/StatusTab';
import { UpdatesTab } from './tabs/UpdatesTab';
import { LogsTab } from './tabs/LogsTab';
import { EditorTab } from './tabs/EditorTab';
import { UsageTab } from './tabs/UsageTab';
import { useSiteStatus, useStartLocalSite } from './hooks';
import { AddStagingForm } from './AddStagingForm';

const ID = 'em-mgr--site--drilldown';
const META_ID = 'em-mgr--site--detail-meta';
const START_ID = 'em-mgr--site--local-start';

export type TabKey = 'status' | 'updates' | 'logs' | 'editor' | 'usage';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'status', label: 'Status' },
  { key: 'updates', label: 'Updates' },
  { key: 'logs', label: 'Logs' },
  { key: 'editor', label: 'Editor' },
  { key: 'usage', label: 'Usage' },
];

/** Valid tab keys, for the route's param validation. */
export const TAB_KEYS: readonly TabKey[] = TABS.map((t) => t.key);

export function isTabKey(value: unknown): value is TabKey {
  return typeof value === 'string' && (TAB_KEYS as readonly string[]).includes(value);
}

export interface SiteDrilldownViewProps {
  siteId: string;
  /** Active tab. Defaults to 'status' so router-free unit tests keep working. */
  tab?: TabKey;
  /** Navigate to another tab. Omitted in unit tests → local fallback state. */
  onTabChange?: (tab: TabKey) => void;
  /** Selected log channel, when the logs tab is active. */
  channel?: string | null;
  onChannelChange?: (channel: string | null) => void;
  /** In-flight update job (?job=), when the updates tab is active. */
  jobId?: string | null;
  onJobChange?: (jobId: string | null) => void;
  /** Expanded conversation (?session=), when the editor tab is active. */
  session?: string | null;
  onSessionChange?: (session: string | null) => void;
}

export function SiteDrilldownView({
  siteId,
  tab = 'status',
  onTabChange,
  channel = null,
  onChannelChange,
  jobId = null,
  onJobChange,
  session = null,
  onSessionChange,
}: SiteDrilldownViewProps) {
  const { data: status } = useSiteStatus(siteId);
  const startLocal = useStartLocalSite(siteId);
  // No onTabChange (standalone unit tests) → tab state stays local; under the
  // router it's always supplied, so the URL is the single source of truth.
  const [localTab, setLocalTab] = useState<TabKey>(tab);
  const activeTab = onTabChange ? tab : localTab;
  const selectTab = onTabChange ?? setLocalTab;

  // Offer "start" only for a local site with stopped containers ('disconnected');
  // 'never' means no containers exist to start.
  const canStart = status?.environment === 'local' && status?.connection === 'disconnected';

  return (
    <section data-id={ID} className="space-y-4">
      <h2 className="text-lg font-semibold">Site: {siteId}</h2>

      {canStart && (
        <div data-id={START_ID} className="flex items-center gap-3">
          <button
            type="button"
            data-id={`${START_ID}--button`}
            onClick={() => startLocal.mutate()}
            disabled={startLocal.isPending}
            className="rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-300"
          >
            {startLocal.isPending ? 'Starting…' : 'Start Docker container'}
          </button>
          {startLocal.isError && (
            <span data-id={`${START_ID}--error`} className="text-sm text-red-600 dark:text-red-400">
              {startLocal.error instanceof Error ? startLocal.error.message : 'Start failed'}
            </span>
          )}
        </div>
      )}

      {status && (status.name || status.localPath || status.localContainer) && (
        <dl data-id={META_ID} className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
          {status.name && (
            <div>
              <dt className="text-slate-500">Name</dt>
              <dd data-id={`${META_ID}--name`} className="font-medium">
                {status.name}
              </dd>
            </div>
          )}
          {status.localPath && (
            <div>
              <dt className="text-slate-500">Local path</dt>
              <dd data-id={`${META_ID}--local-path`} className="font-mono text-xs font-medium">
                {status.localPath}
              </dd>
            </div>
          )}
          {status.localContainer && (
            <div>
              <dt className="text-slate-500">Local container</dt>
              <dd data-id={`${META_ID}--local-container`} className="font-mono text-xs font-medium">
                {status.localContainer}
              </dd>
            </div>
          )}
        </dl>
      )}

      <div className="flex gap-1 border-b border-slate-200 dark:border-slate-800" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={activeTab === t.key}
            data-id={`em-mgr--site--tab--${t.key}`}
            onClick={() => selectTab(t.key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              activeTab === t.key
                ? 'border-slate-900 text-slate-900 dark:border-slate-100 dark:text-slate-100'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div>
        {activeTab === 'status' && <StatusTab siteId={siteId} />}
        {activeTab === 'updates' && (
          <UpdatesTab siteId={siteId} jobId={jobId} onJobChange={onJobChange} />
        )}
        {activeTab === 'logs' && (
          <LogsTab siteId={siteId} channel={channel} onChannelChange={onChannelChange} />
        )}
        {activeTab === 'editor' && (
          <EditorTab siteId={siteId} session={session} onSessionChange={onSessionChange} />
        )}
        {activeTab === 'usage' && <UsageTab siteId={siteId} />}
      </div>

      <AddStagingForm siteId={siteId} />
    </section>
  );
}
